import { ReactElement, useMemo } from 'react';
import styles from './map-popup.module.scss';
import Map, { Layer, Marker, Source } from 'react-map-gl/mapbox';
import circle from '@turf/circle';
import { blue } from '@/ui/styles';
import { UIText, UITextButton, UIToast } from '@/ui';
import { useFormatDate, useCommonTranslation } from '../hooks';
import { uuidv4 } from '../util';
import { MAPBOX_TOKEN } from '@/config';

const ZOOM = 18;
const beaconSize = 15;

type MapPopupProps = {
    lat: number;
    lng: number;
    acc?: number;
    timestamp?: Date;
};
export function MapPopup(props: MapPopupProps): ReactElement {
    const { lat, lng, acc, timestamp } = props;
    const { formatSmartShortTimeAndDate } = useFormatDate();
    const { t } = useCommonTranslation();

    function copyToClipboard(text: string): void {
        navigator.clipboard
            .writeText(text)
            .then(() => {
                UIToast.success(t('components.map_popup.copied'));
            })
            .catch(() => {
                UIToast.error(t('components.map_popup.copy_failed'));
            });
    }

    const latLngText = `${lat.toFixed(5)}, ${lng.toFixed(5)}`;

    const radiusSourceId = useMemo(() => {
        return uuidv4();
    }, []);

    return (
        <div
            style={{
                width: 300,
                overflow: 'hidden',
                borderRadius: 8
            }}
        >
            <div
                style={{
                    width: 300,
                    height: 300
                }}
            >
                <Map
                    style={{ width: '100%', height: '100%' }}
                    mapStyle="mapbox://styles/mapbox/streets-v12"
                    mapboxAccessToken={MAPBOX_TOKEN}
                    initialViewState={{
                        latitude: lat,
                        longitude: lng,
                        zoom: ZOOM
                    }}
                    scrollZoom={{ around: 'center' }}
                >
                    <Marker longitude={lng} latitude={lat}>
                        <div
                            style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                width: beaconSize,
                                height: beaconSize,
                                backgroundColor: blue,
                                borderRadius: 999
                            }}
                        >
                            <div
                                style={{
                                    width: beaconSize / 3,
                                    height: beaconSize / 3,
                                    backgroundColor: 'white',
                                    borderRadius: 999
                                }}
                            />
                        </div>
                    </Marker>
                    {acc !== undefined && (
                        <Source
                            id={`circleSource-${radiusSourceId}`}
                            type="geojson"
                            data={circle([lng, lat], acc, {
                                steps: 100,
                                units: 'meters'
                            })}
                        >
                            <Layer
                                id={`circleLayer-${radiusSourceId}`}
                                type="fill"
                                paint={{
                                    'fill-color': blue,
                                    'fill-opacity': 0.3
                                }}
                            />
                        </Source>
                    )}
                </Map>
            </div>
            <div className={styles.contentContainer}>
                <div className={styles.contentItem}>
                    <div className={styles.contentItemHeader}>
                        <UIText type="default2">
                            {t('components.map_popup.lat_lng_label')}
                        </UIText>
                        <UITextButton
                            variant="primary"
                            onClick={() => {
                                copyToClipboard(latLngText);
                            }}
                        >
                            {t('shared.labels.copy')}
                        </UITextButton>
                    </div>
                    <UIText type="default">{latLngText}</UIText>
                </div>
                {acc !== undefined && (
                    <div className={styles.contentItem}>
                        <div className={styles.contentItemHeader}>
                            <UIText type="default2">
                                {t('components.map_popup.accuracy_label')}
                            </UIText>
                        </div>
                        <UIText type="default">
                            {t('components.map_popup.accuracy', {
                                value: acc.toFixed(1)
                            })}
                        </UIText>
                    </div>
                )}
                {timestamp !== undefined && (
                    <div className={styles.contentItem}>
                        <UIText variant="small" type="default2">
                            {t('components.map_popup.updated', {
                                time: formatSmartShortTimeAndDate(timestamp)
                            })}
                        </UIText>
                    </div>
                )}
            </div>
        </div>
    );
}
